/**
 * ResultsPaneDivider — the horizontal separator between a pane and the
 * results panel below it, shared by the Query Editor (editor / results) and
 * the Visual Query Editor tab (clause cards / results) so both splits look and
 * behave the same. CH-13: 1px `--divider` hairline + 6px invisible hit area,
 * hover/drag pinned to the identical `--primary` tint, pointer-capture + rAF
 * drag, keyboard resize, double-click reset, `role="separator"`. Drive it
 * with `useResultsPaneDivider`.
 */
import type { KeyboardEvent, PointerEvent } from 'react';

export interface ResultsPaneDividerProps {
  /** Height of the pane ABOVE the divider, as a percentage of the container. */
  readonly topPercent: number;
  readonly isDragging: boolean;
  readonly onPointerDown: (e: PointerEvent<HTMLDivElement>) => void;
  readonly onDoubleClick: () => void;
  readonly onKeyDown: (e: KeyboardEvent<HTMLElement>) => void;
  readonly ariaLabel: string;
  /** Names the upper pane in `aria-valuetext` ("60% editor"). */
  readonly topPaneName: string;
  readonly testId: string;
}

export function ResultsPaneDivider({
  topPercent,
  isDragging,
  onPointerDown,
  onDoubleClick,
  onKeyDown,
  ariaLabel,
  topPaneName,
  testId,
}: ResultsPaneDividerProps) {
  const rounded = Math.round(topPercent);
  return (
    <div
      className="flex-shrink-0 h-1.5 cursor-row-resize flex items-center justify-center relative bg-transparent before:absolute before:inset-x-0 before:top-1/2 before:-translate-y-1/2 before:h-px before:bg-divider before:transition-colors before:content-[''] hover:before:bg-primary/60 data-[dragging=true]:before:bg-primary/60 outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
      onPointerDown={onPointerDown}
      onDoubleClick={onDoubleClick}
      onKeyDown={onKeyDown}
      role="separator"
      aria-orientation="horizontal"
      aria-label={ariaLabel}
      aria-valuenow={rounded}
      aria-valuemin={20}
      aria-valuemax={80}
      aria-valuetext={`${rounded}% ${topPaneName}`}
      tabIndex={0}
      data-dragging={isDragging ? 'true' : undefined}
      data-testid={testId}
      style={{
        touchAction: 'none',
        userSelect: 'none',
      }}
    >
      <div className="w-8 h-1 bg-border rounded-full" />
    </div>
  );
}
